import {
    selectVideoFlightById,
    selectVideoFlightIdsOrderedByDateCreated,
} from "features/entity-caching/flights/selectors"
import { zoomToShowPoints } from "features/map/zoom/thunks"
import store from "integrations/redux/store"
import { AbstractVideoController, VideoControllerData } from "./AbstractVideoController"
import { VideoJob } from "@nova/protobuf"
import { UploadState } from "@nova/slice-multifile-upload"
import { selectUploadsForSet, selectVideoJobByFlightId, VideoState } from "../FMV/VideoSlice"
import { createFMVFileUrl, getLocalVideoData, getV1VideoData } from "../FMV/module"
import { fileManagementApi } from "features/data-management/files/fileManagementApi"
import { outputFile } from "plugins/ColorFinder/VideoColorFinder/ColorFinderFiles"
import { selectActiveWorkspaceId } from "features/data-management/workspace/WorkspaceManagementSlice"
import { selectActivatedFireId } from "features/map/control/selectors"
import { decode } from "@msgpack/msgpack"
import { VideoMetadata } from "@nova/slice-video"
import { droneIconColors } from "features/map/layers/video/VideoDroneLayer"

export class VideoFlightController extends AbstractVideoController {
    private videoIsInitialized = false
    private job?: VideoJob
    private uploads?: UploadState<VideoState>[]
    private videoResolutions: string[] = []
    private currentResolutionIndex = 0
    private _canProject = false

    constructor({ flightId }: { flightId: string }) {
        super({ flightId })
        const orderedLiveFlightIds = selectVideoFlightIdsOrderedByDateCreated(store.getState())
        const thisFlightIndex = orderedLiveFlightIds.findIndex((flight) => flight.id === flightId)
        if (thisFlightIndex === -1) {
            throw new Error("Initialized VideoFlightController for flight that does not exist")
        }
        this.droneIconColor = droneIconColors[thisFlightIndex % droneIconColors.length]
    }

    override async initialize() {
        this.videoIsInitialized = true
    }
    override async deinitialize() {
        if (this.videoIsInitialized) {
            this.videoIsInitialized = false
        }
    }

    private detach: () => void = () => {}
    private rAFId?: number
    override attachToElement(element: HTMLVideoElement) {
        this.videoElement = element
        const attach = () => {
            if (this.videoResolutions.length) {
                element.controls = true
                element.loop = true
                const updateProgress = () => {
                    const progress = element.duration ? element.currentTime / element.duration : 0
                    if (this.videoProgress !== progress) {
                        this.videoProgress = progress
                        this.emit("videoProgressChanged")
                    }
                    this.rAFId = requestAnimationFrame(updateProgress)
                }
                const startLoop = () => {
                    if (!this.rAFId) {
                        this.rAFId = requestAnimationFrame(updateProgress)
                    }
                }
                const stopLoop = () => {
                    if (this.rAFId) {
                        cancelAnimationFrame(this.rAFId)
                        this.rAFId = undefined
                    }
                }
                const handlePlayStart = () => {
                    this.isPlaying = true
                    this.hasPlayed = true
                    this.emit("isPlayingChanged")
                    startLoop()
                    element.removeEventListener("playing", handlePlayStart)
                }
                const handlePlay = () => {
                    this.isPlaying = true
                    this.emit("isPlayingChanged")
                    startLoop()
                }
                const handlePause = () => {
                    this.isPlaying = false
                    this.emit("isPlayingChanged")
                    stopLoop()
                }
                const setVideoSrc = () => {
                    element.src = this.videoResolutions[this.currentResolutionIndex]
                }
                element.addEventListener("playing", handlePlayStart)
                element.addEventListener("play", handlePlay)
                element.addEventListener("pause", handlePause)
                this.on("videoResolutionIndexChanged", setVideoSrc)
                setVideoSrc()
                this.detach = () => {
                    stopLoop()
                    element?.removeEventListener("playing", handlePlayStart)
                    element?.removeEventListener("play", handlePlay)
                    element?.removeEventListener("pause", handlePause)
                    this.off("videoResolutionIndexChanged", setVideoSrc)
                }
            }
        }
        this.on("videoTrackChanged", attach)
        attach()
        return () => {
            this.off("videoTrackChanged", attach)
            this.detach()
        }
    }

    override async fetchAllData(zoomTo?: true) {
        const state = store.getState()
        this.job = selectVideoJobByFlightId(this.flightId)(state)
        this.uploads = selectUploadsForSet(this.flightId)(state)
        let fetcher: () => Promise<{ resolutions: string[]; metadata: VideoMetadata }>
        if (!this.job || "mock" in this.job) {
            fetcher = this.fetchLocalData.bind(this)
        } else if ("distance" in this.job) {
            fetcher = this.fetchV1Data.bind(this)
        } else {
            fetcher = this.fetchV2Data.bind(this)
        }
        const { resolutions, metadata } = await fetcher()
        this.videoResolutions = resolutions.toReversed()
        if (resolutions.length > 1) {
            this.currentResolutionIndex = 1
            this.emit("videoResolutionIndexChanged")
        }
        this.data =
            metadata?.map((data, i) => {
                return {
                    type: "liveVideo",
                    timestamp: i,
                    createdAt: `${i}`,
                    longitude: data[0],
                    latitude: data[1],
                    elevation: data[2],
                    pitch: data[3],
                    yaw: data[4],
                    roll: data[5],
                    fov: data[6],
                    height: data[7],
                    horizontalSpeed: 0,
                    verticalSpeed: 0,
                    windSpeed: 0,
                    id: "",
                    sk: "",
                } satisfies VideoControllerData
            }) || []
        this._canProject = metadata?.some?.((arr) =>
            arr.every(
                (num, i) =>
                    i === 2 ||
                    i === 7 ||
                    (typeof num === "number" && !Number.isNaN(num)) ||
                    Array.isArray(num)
            )
        )
        if (this.data.length && zoomTo) {
            store.dispatch(
                zoomToShowPoints({
                    points: this.data.map(({ longitude, latitude }) => [longitude, latitude]),
                })
            )
        }
        this.hasFetched = true
        this.emit("dataChanged")
        this.emit("videoTrackChanged")
    }

    private async fetchLocalData() {
        if (!this.uploads) throw new Error("FMV container rendered with no job and no uploads")
        const [url, metadata] = await getLocalVideoData(this.uploads)
        return {
            resolutions: [url],
            metadata: metadata || [],
        }
    }

    private async fetchV1Data() {
        if (!this.job) throw new Error("Fetching FMV V1 data without a job")
        const [url, metadata] = await getV1VideoData(this.job.videoPath)
        return {
            resolutions: [url],
            metadata: metadata || [],
        }
    }

    private async fetchV2Data() {
        if (!this.job) throw new Error("Fetching FMV V2 data without a job")
        const flight = selectVideoFlightById(this.flightId)(store.getState())
        const metadataPath = this.job.metadataPath
        const paths = [this.job.videoPath]
        if (this.job.resolutions) {
            const state = store.getState()
            const workspaceId = selectActiveWorkspaceId()(state)
            const fireId = selectActivatedFireId(state)
            paths.push(
                ...this.job.resolutions.map((e) =>
                    outputFile(
                        workspaceId,
                        fireId,
                        flight?.demoId || this.flightId,
                        e,
                        !!flight?.demoId
                    )
                )
            )
        }
        const [metadata, signedUrls] = await Promise.all([
            (async () => {
                if (!metadataPath) return Promise.resolve(undefined)
                const s3Key = !!flight?.demoId ? `public/sample-data/${metadataPath}` : metadataPath

                const metadataBuffer = await fileManagementApi
                    .fetchFile(s3Key, { isDemo: !!flight?.demoId })
                    .then((r) => r.arrayBuffer())
                const metadata = decode(metadataBuffer) as VideoMetadata
                return metadata
            })(),
            (async () => {
                return await Promise.all(
                    paths.map(async (path) => {
                        const s3Key = !!flight?.demoId ? `public/sample-data/${path}` : path
                        const url = await createFMVFileUrl(s3Key, !!flight?.demoId)
                        return url
                    })
                )
            })(),
        ])
        return {
            resolutions: signedUrls,
            metadata: metadata || [],
        }
    }

    canProject() {
        return this._canProject
    }

    getResolutions() {
        return this.videoResolutions
    }

    getCurrentResolutionIndex() {
        return this.currentResolutionIndex
    }
    setCurrentResolutionIndex(newIndex: number) {
        if (newIndex >= this.videoResolutions.length) {
            console.warn("Tried to set video resolution to an invalid value")
            return
        }
        this.currentResolutionIndex = newIndex
        this.emit("videoResolutionIndexChanged")
    }
}
